import { Button, Callout, ErrorBanner, TextField, icons } from "@bruntworkwear-apps/ui";
import { copyFor, EXPECTED_HINT, EXPECTED_LABEL } from "../../kinds";
import { KindPickerView } from "./components/KindPicker.view";
import { LongAnswerView } from "./components/LongAnswer.view";
import { WhatGetsSentView } from "./components/WhatGetsSent.view";
import styles from "./FeedbackDialog.module.css";
import type { FeedbackDialogProps, FeedbackFormValues } from "./FeedbackDialog.types";

interface FeedbackDialogViewProps extends FeedbackDialogProps, FeedbackFormValues {
  userEmail: string | null;
  hasRun: boolean;
  logCount: number;
  summaryError: string | null;
  canSubmit: boolean;
  onKind: (kind: FeedbackFormValues["kind"]) => void;
  onSummary: (value: string) => void;
  onDetail: (value: string) => void;
  onExpected: (value: string) => void;
  onSend: () => void;
}

/** Markup for the report form. Presentation only. */
export function FeedbackDialogView(props: FeedbackDialogViewProps) {
  const { isSending, limits, kind } = props;
  const copy = copyFor(kind);

  if (props.isSessionExpired) {
    return (
      <div className={styles.body}>
        <Callout tone="warning">
          You have been signed out, so this was not sent. Sign in and your report will be waiting -
          nothing you typed is lost until you close this.
        </Callout>
        <div className={styles.actions}>
          <Button onClick={props.onReauthenticate}>Sign in again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      {/* Before they type, not after. Somebody testing the form deserves to know
          it is a dry run while they still have the choice of not bothering. */}
      {limits.is_dry_run && (
        <Callout tone="warning">
          Local development: nothing is filed. The report is rendered into the API log so you can
          read the issue it would have created.
        </Callout>
      )}

      <KindPickerView value={kind} disabled={isSending} onChange={props.onKind} />

      <TextField
        label={copy.summaryLabel}
        value={props.summary}
        error={props.summaryError}
        hint="This is what the team sees first, so keep it short."
        placeholder={copy.summaryPlaceholder}
        maxLength={limits.max_summary_length}
        disabled={isSending}
        onChange={(event) => props.onSummary(event.target.value)}
      />

      <LongAnswerView
        label={copy.detailLabel}
        hint={copy.detailHint}
        placeholder={copy.detailPlaceholder}
        value={props.detail}
        maxLength={limits.max_detail_length}
        disabled={isSending}
        onChange={props.onDetail}
      />

      {/* Only for a bug. There is no expected behavior to state for an idea or
          a question, so asking would be an empty box beside a real one. */}
      {kind === "bug" && (
        <LongAnswerView
          label={EXPECTED_LABEL}
          hint={EXPECTED_HINT}
          placeholder="The spreadsheet should have downloaded."
          value={props.expected}
          maxLength={limits.max_detail_length}
          disabled={isSending}
          onChange={props.onExpected}
        />
      )}

      <WhatGetsSentView
        userEmail={props.userEmail}
        hasRun={props.hasRun}
        logCount={props.logCount}
      />

      <ErrorBanner message={props.error} />

      <div className={styles.actions}>
        <Button
          onClick={props.onSend}
          disabled={!props.canSubmit}
          loading={isSending}
          leadingIcon={<icons.ArrowRight size={16} aria-hidden />}
        >
          {isSending ? "Sending…" : copy.submitLabel}
        </Button>
        <Button variant="ghost" onClick={props.onClose} disabled={isSending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
